import React, { useState } from 'react';
import { Check, X, ArrowRight } from 'lucide-react';

const PricingPage = ({ onGetStarted }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  const tiers = [
    { name: 'Free', sub: 'Test the waters', monthly: 0, annual: 0,
      features: [
        { f: '3 articles (lifetime)', on: true }, { f: '10 credits', on: true },
        { f: 'Basic SEO analysis', on: true }, { f: 'AEO/GEO scoring', on: true },
        { f: '1 platform connection', on: true },
        { f: 'Voice-to-Article AI', on: false }, { f: 'Brand voice profiles', on: false },
        { f: 'Brand reporting & kit', on: false }, { f: 'AI assistant', on: false },
      ], cta: 'Start free' },
    { name: 'Starter', sub: 'For the overwhelmed owner', monthly: 29, annual: 290,
      features: [
        { f: '10 articles/month', on: true }, { f: '100 credits', on: true },
        { f: 'AI article generation', on: true }, { f: 'AEO/GEO scoring', on: true },
        { f: '3 platform connections', on: true }, { f: 'Content calendar', on: true },
        { f: 'AI assistant', on: true }, { f: 'Email support', on: true },
        { f: 'Voice-to-Article AI', on: false }, { f: 'Brand voice profiles', on: false },
      ], cta: 'Start 14-day trial' },
    { name: 'Professional', sub: 'For growth marketers', monthly: 79, annual: 790, featured: true,
      features: [
        { f: '50 articles/month', on: true }, { f: '500 credits', on: true },
        { f: 'Voice-to-Article AI', on: true }, { f: 'Full AEO/GEO optimization', on: true },
        { f: 'All platform integrations', on: true }, { f: 'Brand voice profiles (3)', on: true },
        { f: 'Brand reporting & kit', on: true }, { f: 'Content calendar + scheduling', on: true },
        { f: 'Advanced analytics', on: true }, { f: 'Priority support', on: true },
      ], cta: 'Go Pro — 14-day trial' },
    { name: 'Enterprise', sub: 'For agencies', monthly: 199, annual: 1990,
      features: [
        { f: '200 articles/month', on: true }, { f: '2,000 credits', on: true },
        { f: 'Everything in Professional', on: true }, { f: 'Unlimited brand profiles', on: true },
        { f: 'Custom AI workflows', on: true }, { f: 'White-label options', on: true },
        { f: 'Team collaboration', on: true }, { f: 'Dedicated account manager', on: true },
        { f: 'SLA guarantee', on: true },
      ], cta: 'Talk to sales' },
  ];

  const faq = [
    { q: 'What are credits used for?', a: 'Credits are consumed when you use AI features: article generation (2.5), SEO analysis (0.5), AI chat (1), and voice sessions (5). Credits never expire.' },
    { q: 'Can I switch plans?', a: 'Yes, you can upgrade or downgrade at any time. Changes take effect at your next billing cycle. We prorate any remaining balance.' },
    { q: 'Is there a free trial?', a: 'All paid plans include a 14-day free trial. No credit card required to start with the free plan.' },
    { q: 'What happens when I hit the article limit?', a: "You can always purchase additional credit packs. Or upgrade your plan for a higher monthly article allowance." },
    { q: 'Do credits expire?', a: 'No, purchased credits never expire. Monthly credits from your subscription refresh each billing cycle.' },
    { q: 'How does annual billing work?', a: 'Pay upfront for 12 months and get 2 months free. That\'s a ~17% saving. You can switch between monthly and annual at any time.' },
  ];

  return (
    <div>
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">PRICING</span>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">Simple, transparent pricing</h1>
            <p className="text-base text-[var(--color-muted)] mb-8">Start free. Upgrade when you're ready. No hidden fees.</p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-3 bg-[var(--color-surface)] rounded-full p-1.5" data-testid="billing-toggle">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-5 py-2 rounded-full text-xs font-medium transition-all ${
                  !isAnnual ? 'bg-[var(--color-charcoal)] text-white shadow-sm' : 'text-[var(--color-muted)] hover:text-[var(--color-charcoal)]'
                }`}
                data-testid="billing-monthly-btn"
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-5 py-2 rounded-full text-xs font-medium transition-all relative ${
                  isAnnual ? 'bg-[var(--color-charcoal)] text-white shadow-sm' : 'text-[var(--color-muted)] hover:text-[var(--color-charcoal)]'
                }`}
                data-testid="billing-annual-btn"
              >
                Annual
                <span className="absolute -top-2 -right-2 bg-[var(--color-coral)] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                  -17%
                </span>
              </button>
            </div>
            {isAnnual && (
              <p className="text-[10px] text-[var(--color-coral)] mt-2 font-medium" data-testid="annual-savings-note">
                2 months free on all paid plans
              </p>
            )}
          </div>
          
          <div className="grid md:grid-cols-4 gap-5 mb-12">
            {tiers.map((tier, idx) => {
              const price = isAnnual && tier.annual > 0
                ? (tier.annual / 12).toFixed(0)
                : tier.monthly;
              const totalAnnual = tier.annual;
              const monthlySavings = tier.monthly > 0 ? (tier.monthly * 12 - tier.annual) : 0;

              return (
                <div key={idx} className={`pricing-card ${tier.featured ? 'featured' : ''}`} data-testid={`pricing-card-${tier.name.toLowerCase()}`}>
                  {tier.featured && <div className="font-label text-[var(--color-coral)] mb-3">MOST POPULAR</div>}
                  <div className="pricing-tier">{tier.name}</div>
                  <p className="text-[10px] text-[var(--color-muted)] mb-3">{tier.sub}</p>
                  <div className="pricing-price">
                    &#8364;{price}
                    <span>/mo</span>
                  </div>
                  {isAnnual && tier.annual > 0 && (
                    <div className="mb-3 -mt-1">
                      <p className="text-[9px] text-[var(--color-muted)]">
                        &#8364;{totalAnnual}/year &middot; Save &#8364;{monthlySavings}
                      </p>
                    </div>
                  )}
                  <div className="pricing-features">
                    {tier.features.map((f, fidx) => (
                      <div key={fidx} className={`pricing-feature ${!f.on ? 'opacity-40' : ''}`}>
                        {f.on ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        <span>{f.f}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={onGetStarted} className={`btn w-full ${tier.featured ? 'btn-accent' : 'btn-secondary'}`}>{tier.cta}</button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-[var(--color-muted)] mb-2">
            <strong>Credits never expire.</strong> Top up: 100 credits &#8364;9 &middot; 500 credits &#8364;39 &middot; 1,000 credits &#8364;69
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faq.map((item, idx) => (
              <div key={idx} className="card p-5">
                <h3 className="font-display text-sm font-medium mb-2">{item.q}</h3>
                <p className="text-xs text-[var(--color-muted)] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
